import { ChangeDetectionStrategy, Component, DestroyRef, inject, input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { injectMutation } from '@tanstack/angular-query-experimental';
import { Bucket } from '../../types';
import { EMPTY, from, Observable } from 'rxjs';
import { debounceTime, filter, map, concatMap } from 'rxjs/operators';
import { BucketService, toBucketUpdateDraft } from '../../services/bucket.service';
import { FormControl, FormGroup, AbstractControl, Validators, ReactiveFormsModule } from '@angular/forms';
import { UiFieldComponent } from '../../ui/field/field.component';
import { UiCurrencyInputComponent } from '../../ui/currency-input/currency-input.component';

import { UiButtonDirective } from '../../ui/button/button.directive';
import { bucketTypeAmountLabel, bucketTypeGoalLabel, BucketTypeOptionValue } from '../bucket-type-options';

type BucketSetupFormValue = {
  amount: number | null;
  goal: number | null;
};

@Component({
  selector: 'app-bucket-setup-toolbar',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './bucket-setup-toolbar.component.html',
  styleUrl: './bucket-setup-toolbar.component.scss',
  imports: [ReactiveFormsModule, UiFieldComponent, UiCurrencyInputComponent, UiButtonDirective],
})
export class BucketSetupToolbarComponent implements OnInit {
  private bucketService = inject(BucketService);

  readonly bucket = input.required<Bucket>();
  private readonly destroyRef = inject(DestroyRef);
  readonly updateBucketMutation = injectMutation(() => this.bucketService.updateBucketMutationOptions());
  formGroup = new FormGroup<{
    amount: FormControl<number | null>;
    goal: FormControl<number | null>;
  }>({
    amount: new FormControl(0, [Validators.required, Validators.pattern('[0-9.]*')]),
    goal: new FormControl(0, [Validators.pattern('[0-9.]*')]),
  });

  constructor() {
    this.formGroup.markAllAsTouched();
  }

  amountLabel(bucket: Bucket): string {
    return bucketTypeAmountLabel(bucket.type as BucketTypeOptionValue);
  }

  goalLabel(): string {
    return bucketTypeGoalLabel();
  }

  ngOnInit() {
    const bucket = this.bucket();
    this.formGroup.setValue({ amount: bucket.amount, goal: bucket.fund?.goal ?? null });
    this.formGroup.valueChanges
      .pipe(
        filter(() => this.formGroup.valid),
        debounceTime(200),
        map(() => this.formGroup.getRawValue()),
        concatMap(value => this.saveBucketUpdate$(value)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private saveBucketUpdate$({ amount, goal }: BucketSetupFormValue): Observable<Bucket> {
    const bucket = this.bucket();
    if (goal === (bucket.fund?.goal ?? null) && amount === bucket.amount) return EMPTY;

    return from(
      this.updateBucketMutation.mutateAsync(
        toBucketUpdateDraft(bucket, {
          amount: amount ?? 0,
          fund: bucket.fund ? { ...bucket.fund, goal: goal ?? undefined } : bucket.fund,
        }),
      ),
    );
  }

  onFocus(control: AbstractControl) {
    if (control.value === 0) control.setValue(null);
  }

  onFocusOut(control: AbstractControl) {
    if (!control.value) control.setValue(0);
  }

  get bucketTotal(): number {
    const bucket = this.bucket();
    if (bucket.type === 'INCOME') return bucket.total;
    return bucket.total * -1;
  }

  async finishSetup(): Promise<void> {
    const bucket = this.bucket();
    await this.updateBucketMutation.mutateAsync(toBucketUpdateDraft(bucket, { isSetup: true }));
  }
}
